import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { userActions } from '../../actions';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as Yup from 'yup';

// eslint-disable-next-line
const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const regexMobile = /^09[0-9]{9}$/i;

const LoginForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const loggingIn = useSelector(state => state.authentication.loggingIn);
  const loggingOut = useSelector(state => state.authentication.loggingOut);
  const loggedIn = useSelector(state => state.authentication.loggedIn);
  const forgotPasswordState = useSelector(state => state.authentication.forgotPasswordState);
  const resetPasswordState = useSelector(state => state.authentication.resetPasswordState);
  const error = useSelector(state => state.authentication.error);
  const [messageContent, setMessageContent] = useState('');
  const [step, setStep] = useState(1);
  const mobileRef = useRef('');

  useEffect(() => {
    if (loggedIn && !loggingOut && user) {
      if (user.role === 'referral') {
        history.push('/agents');
      } else {
        history.push('/dashboard');
      }
    }

    if (error) {
      switch (error.status) {
        case 401:
          setMessageContent(t('loginError401'));
          break;
        case 404:
          setMessageContent(t('loginError404'));
          break;
        case 423:
          setMessageContent(t('loginError423'));
          break;
        case 500:
          setMessageContent(t('error500'));
          break;
        case 502:
          setMessageContent(t('error500'));
          break;
        default:
          setMessageContent(t('errorGlobalMessage'));
          break;
      }
    }
  }, [error, history, loggedIn, loggingOut, t, user]);

  useEffect(() => {
    switch (forgotPasswordState) {
      case 'success':
        setMessageContent(t('forgotPasswordSMS'));
        setStep(3);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 404:
            setMessageContent(t('userNotFound'));
            break;
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
      default:
        break;
    }
  }, [error, forgotPasswordState, t]);

  useEffect(() => {
    switch (resetPasswordState) {
      case 'success':
        setMessageContent(t('passwordChangedSuccess'));
        setStep(1);
        break;
      case 'failure':
        switch (_.get(error, 'status', '')) {
          case 500:
            setMessageContent(t('error500'));
            break;
          default:
            setMessageContent(t('errorGlobalMessage'));
            break;
        }
        break;
      default:
        break;
    }
  }, [error, resetPasswordState, t]);

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .required(t('yupUsernameReq'))
      .matches(new RegExp(`${regexEmail.source}|${regexMobile.source}`), t('yupUsernameRegex')),
    password: Yup.string().required(t('yupPasswordReq')),
  });

  const MobileSchema = Yup.object().shape({
    mobile: Yup.string()
      .required(t('yupMobileReq'))
      .matches(new RegExp(regexMobile.source), t('yupMobileRegex')),
  });

  const ResetPassSchema = Yup.object().shape({
    code: Yup.number()
      .min(6, t('yupConfirmCodeValidate'))
      .required(t('yupConfirmCodeReq')),
    password: Yup.string()
      .min(6, t('yupPasswordValidate'))
      .required(t('yupPasswordReq')),
    confirmPassword: Yup.string()
      .required(t('yupConfirmPassReq'))
      .oneOf([Yup.ref('password'), null], t('yupConfirmPassValidate')),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: values => {
      if (values.username && values.password) {
        dispatch(userActions.login(values.username, values.password));
      }
    },
  });

  const mobileFormik = useFormik({
    initialValues: {
      mobile: '',
    },
    validationSchema: MobileSchema,
    onSubmit: values => {
      mobileRef.current = values.mobile;
      dispatch(userActions.forgotPassword(values));
    },
  });

  const resetPassFormik = useFormik({
    initialValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: ResetPassSchema,
    onSubmit: values => {
      dispatch(
        userActions.resetPassword({
          mobile: mobileRef.current,
          code: values.code,
          password: values.password,
        }),
      );
    },
  });

  const messageIsSuccess =
    forgotPasswordState === 'success' || resetPasswordState === 'success';
  const showMessage = messageIsSuccess || Boolean(error);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <Alert
            className="enteryFormMessage"
            variant={messageIsSuccess ? 'success' : 'danger'}
            hidden={!showMessage}
          >
            {messageContent}
          </Alert>
          {step === 1 && (
            <>
              {/* <h2 textAlign="center" >{t('loginHeader')}</h2> */}
              <form className="VForm" onSubmit={formik.handleSubmit}>
                <div>
                  <input
                    aria-invalid={Boolean(formik.touched.username && formik.errors.username)}
                    disabled={loggingIn}
                    required
                    placeholder={t('username')}
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                  />
                  <input
                    aria-invalid={Boolean(formik.touched.password && formik.errors.password)}
                    disabled={loggingIn}
                    required
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                  />

                  {/* <button type="submit" className="btn btn-primary">Submit</button> */}
                  <button
                    className="btn btn-primary"
                    aria-busy={loggingIn}
                    disabled={loggingIn}
                    type='submit'
                  >
                    {t('login')}
                  </button>
                  {/* <Button basic="true" color="blue" fluid size="large">
                  <Icon name="google" color="red" /> ورود با حساب کاربری گوگل
                </Button> */}
                </div>
              </form>
            </>
          )}
          {step === 2 && (
            <>
              <h2>{t('forgotPassword')} </h2>
              <form className="VForm" onSubmit={mobileFormik.handleSubmit}>
                <div>
                  <input
                    aria-invalid={Boolean(mobileFormik.touched.mobile && mobileFormik.errors.mobile)}
                    disabled={forgotPasswordState === 'request'}
                    required
                    placeholder={t('mobile')}
                    name="mobile"
                    value={mobileFormik.values.mobile}
                    onChange={mobileFormik.handleChange}
                  />

                  <button
                    className="vButton colorPrimary"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    aria-busy={forgotPasswordState === 'request'}
                    disabled={forgotPasswordState === 'request'}
                    type="submit"
                  >
                    {t('confirm')}
                  </button>
                </div>
              </form>
            </>
          )}
          {step === 3 && (
            <>
              <h2>{t('changePassword')} </h2>
              <form className="VForm" onSubmit={resetPassFormik.handleSubmit}>
                <div>
                  <input
                    aria-invalid={Boolean(resetPassFormik.touched.code && resetPassFormik.errors.code)}
                    disabled={resetPasswordState === 'request'}
                    required
                    placeholder={t('code')}
                    type="text"
                    name="code"
                    value={resetPassFormik.values.code}
                    onChange={resetPassFormik.handleChange}
                  />
                  <input
                    aria-invalid={Boolean(
                      resetPassFormik.touched.password && resetPassFormik.errors.password
                    )}
                    disabled={resetPasswordState === 'request'}
                    required
                    placeholder={t('password')}
                    type="password"
                    name="password"
                    value={resetPassFormik.values.password}
                    onChange={resetPassFormik.handleChange}
                  />

                  <input
                    aria-invalid={Boolean(
                      resetPassFormik.touched.confirmPassword
                      && resetPassFormik.errors.confirmPassword
                    )}
                    disabled={resetPasswordState === 'request'}
                    required
                    placeholder={t('confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    value={resetPassFormik.values.confirmPassword}
                    onChange={resetPassFormik.handleChange}
                  />

                  <button
                    className=" vButton colorPrimary"
                    style={{ marginTop: 45, marginBottom: 15 }}
                    aria-busy={resetPasswordState === 'request'}
                    disabled={resetPasswordState === 'request'}
                    type="submit"
                  >
                    {t('confirm')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      {step === 1 && (
        <>
          {/* <div className="textBox">
            <span className="link" onClick={() => setStep(2)}>
              {t('forgotPassword')}
            </span>
          </div>
          <div className="textBox">
            <span>{t('didNotRegisterYet')}</span>
            <span className="link" onClick={props.gotoRegister}>
              {t('register')}
            </span>
          </div> */}
        </>
      )}
      {(step === 2 || step === 3) && (
        <div className="textBox">
          <span className="link" onClick={() => setStep(1)}>
            {t('returnToLogin')}
          </span>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
